import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import './App.css';

function App() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Négyzet állandó értékei (középen)
  const [squareSize] = useState(200);

  // Kör state
  const [circle, setCircle] = useState({
    x: 150,
    y: 150,
    radius: 56
  });

  // Interakció state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFinalized, setIsFinalized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Iframe magasság küldése a szülőnek (automatikus magasság)
  useEffect(() => {
    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'iframe-resize', height }, '*');
    };
    sendHeight();
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', sendHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, []);

  // Canvas méret számítása
  const canvasWidth = 800;
  const canvasHeight = 600;
  const squareX = (canvasWidth - squareSize) / 2;
  const squareY = (canvasHeight - squareSize) / 2;

  // Rajzolás
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Négyzet rajzolása (fekete vonal)
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    ctx.setLineDash([]);
    ctx.strokeRect(squareX, squareY, squareSize, squareSize);

    // Kör rajzolása
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(circle.x, circle.y, circle.radius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#4BAC9B';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Ha nem véglegesítve, kis fogantyúk a kör szélén az átméretezéshez
    if (!isFinalized) {
      ctx.fillStyle = '#EF4444';
      const handlePositions = [
        { x: circle.x + circle.radius, y: circle.y }, // jobb
        { x: circle.x - circle.radius, y: circle.y }, // bal
        { x: circle.x, y: circle.y + circle.radius }, // lent
        { x: circle.x, y: circle.y - circle.radius }  // fent
      ];

      handlePositions.forEach(pos => {
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 6, 0, 2 * Math.PI);
        ctx.fill();
      });
    }
  }, [circle, canvasWidth, canvasHeight, squareSize, squareX, squareY, isFinalized]);

  // Egér pozíció lekérése canvas relatív koordinátákban
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Ellenőrzi, hogy az egér a körön belül van-e
  const isInsideCircle = (mouseX, mouseY) => {
    const distance = Math.sqrt(
      Math.pow(mouseX - circle.x, 2) + Math.pow(mouseY - circle.y, 2)
    );
    return distance <= circle.radius;
  };

  // Ellenőrzi, hogy az egér a kör szélén van-e (átméretezéshez)
  const isOnCircleEdge = (mouseX, mouseY) => {
    const distance = Math.sqrt(
      Math.pow(mouseX - circle.x, 2) + Math.pow(mouseY - circle.y, 2)
    );
    return Math.abs(distance - circle.radius) <= 10;
  };

  const handleMouseDown = (e) => {
    if (isFinalized) return;

    const pos = getMousePos(e);

    if (isOnCircleEdge(pos.x, pos.y)) {
      setIsResizing(true);
      setDragStart(pos);
    } else if (isInsideCircle(pos.x, pos.y)) {
      setIsDragging(true);
      setDragStart({ x: pos.x - circle.x, y: pos.y - circle.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isFinalized) return;

    const pos = getMousePos(e);

    if (isDragging) {
      setCircle(prev => ({
        ...prev,
        x: pos.x - dragStart.x,
        y: pos.y - dragStart.y
      }));
    } else if (isResizing) {
      const newRadius = Math.sqrt(
        Math.pow(pos.x - circle.x, 2) + Math.pow(pos.y - circle.y, 2)
      );
      setCircle(prev => ({
        ...prev,
        radius: Math.max(20, Math.min(300, newRadius))
      }));
    } else {
      // Kurzor változtatás
      const canvas = canvasRef.current;
      if (isOnCircleEdge(pos.x, pos.y)) {
        canvas.style.cursor = 'nwse-resize';
      } else if (isInsideCircle(pos.x, pos.y)) {
        canvas.style.cursor = 'move';
      } else {
        canvas.style.cursor = 'default';
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleFinalize = () => {
    setIsFinalized(true);
  };

  const handleReset = () => {
    setIsFinalized(false);
    setCircle({
      x: 150,
      y: 150,
      radius: 56
    });
  };

  const toggleFullscreen = () => {
    if (window.self !== window.top) {
      window.parent.postMessage({ type: 'komfortzona-fullscreen', action: 'toggle' }, '*');
      return;
    }
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleSaveImage = async () => {
    // Canvas rajzolás közvetlenül PDF-be
    const pdf = new jsPDF('p', 'mm', 'a4');

    // Fejléc hozzáadása
    pdf.setFontSize(18);
    pdf.setTextColor(75, 172, 155); // #4BAC9B RGB formátumban
    pdf.text('Kör - Négyzet rajz', 105, 15, { align: 'center' });

    // Canvas méretarányok számítása PDF koordinátákhoz (3:4 arány)
    const pdfWidth = 180;
    const offsetX = 15;
    const offsetY = 30;

    // Négyzet méret és pozíció számítása PDF koordinátákban
    const scale = pdfWidth / canvasWidth;
    const squareSizePdf = squareSize * scale;
    const squareXPdf = squareX * scale + offsetX;
    const squareYPdf = squareY * scale + offsetY;

    // Négyzet rajzolása (fekete vonal)
    pdf.setDrawColor(0, 0, 0); // Fekete
    pdf.setLineWidth(0.5);
    pdf.rect(squareXPdf, squareYPdf, squareSizePdf, squareSizePdf);

    // Kör rajzolása
    const circleXPdf = circle.x * scale + offsetX;
    const circleYPdf = circle.y * scale + offsetY;
    const circleRadiusPdf = circle.radius * scale;

    pdf.setDrawColor(75, 172, 155); // #4BAC9B
    pdf.setLineWidth(0.8);
    pdf.circle(circleXPdf, circleYPdf, circleRadiusPdf);

    // Lábléc hozzáadása - aktuális dátum
    const currentDate = new Date().toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    pdf.setFontSize(10);
    pdf.setTextColor(107, 114, 128); // #6b7280
    pdf.text(currentDate, 105, 287, { align: 'center' });

    pdf.save('kor-negyzet-rajz.pdf');
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-title">
          <h1>Kör - Négyzet rajz</h1>
          <p className="subtitle">Rajzolj egy kört a lapra!</p>
        </div>
        <div className="header-actions">
          {!isFinalized ? (
            <button type="button" className="header-btn header-btn-finalize" onClick={handleFinalize}>
              <span className="btn-icon">✓</span>
              Véglegesít
            </button>
          ) : (
            <>
              <button type="button" className="header-btn header-btn-save" onClick={handleSaveImage}>
                <span className="btn-icon">📄</span>
                PDF mentése
              </button>
              <button type="button" className="header-btn header-btn-reset" onClick={handleReset}>
                <span className="btn-icon">↻</span>
                Újrakezdés
              </button>
            </>
          )}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="header-btn fullscreen-btn"
            title={isFullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyő'}
            aria-label={isFullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyő'}
          >
            {isFullscreen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M9 3H5a2 2 0 0 0-2 2v4" />
                <path d="M15 21h4a2 2 0 0 0 2-2v-4" />
                <path d="M21 9V5a2 2 0 0 0-2-2h-4" />
                <path d="M3 15v4a2 2 0 0 0 2 2h4" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M8 3H3v5" />
                <path d="M16 3h5v5" />
                <path d="M21 16v5h-5" />
                <path d="M3 16v5h5" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <div className="canvas-container" ref={containerRef}>
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>

      <div className="instructions">
        <h3>Használati útmutató:</h3>
        <ul>
          <li>🖱️ <strong>Mozgatás:</strong> Kattints a körre és húzd az egérrel</li>
          <li>↔️ <strong>Átméretezés:</strong> Fogd meg a kör szélét (piros pontok)</li>
          <li>✓ <strong>Véglegesítés:</strong> Ha készen vagy, kattints a Véglegesít gombra</li>
          <li>📄 <strong>Mentés:</strong> A véglegesítés után mentheted PDF-be (A4 formátum)</li>
        </ul>
      </div>
    </div>
  );
}

export default App;
